import { Phone, MapPin, MessageCircle, Star, ArrowRight, BadgeCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';

const PHONE_NUMBER = "+919971231812";
const WHATSAPP_NUMBER = "919971231812";
const HERO_BG = "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=1200&q=80";

export const Hero = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-[90vh] md:min-h-screen bg-gradient-to-br from-[#FDFBF7] via-[#F0FDFA] to-[#CCFBF1] overflow-hidden"
    >
      {/* Eclipse Authorized Dealer Banner */}
      <div 
        data-testid="eclipse-dealer-banner"
        className="relative bg-gradient-to-r from-[#1a365d] via-[#2c5282] to-[#1a365d] text-white py-3 px-4"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <BadgeCheck className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <img 
            src="https://customer-assets.emergentagent.com/job_unlock-it-2/artifacts/mhgr0ph2_IMG_1563.jpeg"
            alt="Eclipse Logo"
            className="h-8 w-auto"
          />
          <span className="text-sm md:text-base font-medium text-center">
            <span className="text-amber-400 font-bold">Authorised Dealer</span>
            <span className="mx-2">–</span>
            <span className="font-semibold">Eclipse International</span>
            <span className="hidden sm:inline text-white/80 ml-2">| A World of Sleep Since 1905</span>
          </span>
          <BadgeCheck className="w-5 h-5 text-amber-400 flex-shrink-0 hidden sm:block" />
        </div>
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-200 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-100 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[70vh]">
          
          {/* Left Content */}
          <div className="order-2 lg:order-1 text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm mb-6">
              <span className="text-amber-600 font-semibold text-sm">Since 1992</span>
              <span className="text-stone-400">|</span>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-amber-500 fill-current" />
                <span className="text-stone-600 text-sm font-medium">4.5 Rating</span>
              </div>
            </div>

            {/* Headline */}
            <h1 
              data-testid="hero-headline"
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-stone-800 leading-tight mb-6"
            >
              Dream in Style,{' '}
              <span className="text-teal-600">Sleep in Comfort</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-stone-600 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              New Delhi's trusted home furnishing store. Premium mattresses, bedsheets, 
              pillows & more from top brands at the best prices.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <a href={`tel:${PHONE_NUMBER}`} data-testid="hero-call-btn">
                <Button 
                  size="lg"
                  className="w-full sm:w-auto rounded-full bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg font-medium shadow-lg shadow-teal-600/20 hover:scale-105 transition-all"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
              
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
              >
                <Button 
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto rounded-full border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white px-8 py-6 text-lg font-medium transition-all"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </a>
            </div>

            {/* Get Directions */}
            <a 
              href="https://maps.app.goo.gl/tLGnG1nKfKTFJZHaA"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-directions-btn"
              className="inline-flex items-center gap-2 text-stone-600 hover:text-teal-700 font-medium transition-colors"
            >
              <MapPin className="w-5 h-5" />
              <span>Yusuf Sarai Market, New Delhi</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Right Image */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-tl-[60px] md:rounded-tl-[100px] rounded-br-[60px] md:rounded-br-[100px] overflow-hidden shadow-2xl">
                <img 
                  src={HERO_BG}
                  alt="Comfortable bedroom with premium bedding"
                  className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
                  data-testid="hero-image"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>

              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 md:-bottom-8 md:-left-8 bg-white rounded-2xl p-4 md:p-6 shadow-xl max-w-[200px] md:max-w-[250px]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <Star className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-bold text-stone-800">30+ Years</p>
                    <p className="text-xs text-stone-500">of Trust</p>
                  </div>
                </div>
                <p className="text-sm text-stone-600">
                  Family-run business serving Delhi since 1992
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Brands Strip */}
      <div className="relative bg-white/60 backdrop-blur-sm py-6 border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-stone-500 mb-4">Deals in Premium brand</p>
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-stone-700 font-semibold">
            {/* Eclipse - Highlighted */}
            <div className="flex items-center gap-2 bg-gradient-to-r from-[#1a365d] to-[#2c5282] text-white px-4 py-2 rounded-full">
              <BadgeCheck className="w-4 h-4 text-amber-400" />
              <span className="text-base font-bold">Eclipse</span>
            </div>
            <span className="text-stone-300 hidden sm:inline">|</span>
            <span className="text-lg">Sleepwell</span>
            <span className="text-stone-300">|</span>
            <span className="text-lg">King Koil</span>
            <span className="text-stone-300">|</span>
            <span className="text-lg">Kurlon</span>
            <span className="text-stone-300 hidden sm:inline">|</span>
            <span className="text-lg hidden sm:inline">Springwel</span>
            <span className="text-stone-300 hidden md:inline">|</span>
            <span className="text-lg hidden md:inline">Bombay Dyeing</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
